import { createContext } from 'react';

import { type CONTRIBUTION_MODES, type ContentPublicationStatus } from '@lumapps/contents/constants';
import type { Content } from '@lumapps/contents/types';

import { ContentEditorState, initialContentEditorState } from '../ducks/slice';
import type { ContentSave } from '../types';
import type { WidgetPathsMap } from '../utils/getAllWidgetsWithPaths';

export interface ContentEditorActions {
    onInputLanguageChange: (lang: string) => void;
    refreshWidgetsByType?: (widgetTypes: string[]) => void;
    updateContributionMode: (contributionMode: CONTRIBUTION_MODES, shouldStore?: boolean) => void;
    openContentSettings: () => void;
    saveContent: ContentSave;
    updateContent: (
        params: { content: Content; isPartial?: false } | { content: Partial<Content>; isPartial: true },
    ) => void;
    publishContent: () => Promise<Content>;
}

export interface ContentEditorContextValue {
    state: ContentEditorState;
    publicationInformations?: {
        expiredAt?: string;
        publishedAt?: string;
        scheduledAt?: string;
        publicationStatus: ContentPublicationStatus;
        isExpiringSoon: boolean;
        hasPendingDraft: boolean;
        isAwaitingValidation: boolean;
        isRevisionRequested: boolean;
    };
    isLoading?: boolean;
    contentEditorActions: ContentEditorActions;
    /** Map of widgetId → form path + widget type, computed once at mount (layout is immutable in ACW) */
    widgetPaths: WidgetPathsMap;
    /** Derives the URL slug from the title when the user edits the title in one of the title editors. */
    onTitleChange: (lang: string, newValue?: string) => void;
    /** Whether the content is being created (no id yet) rather than edited. */
    isCreation: boolean;
}

export const ContentEditorContext = createContext<ContentEditorContextValue>({
    contentEditorActions: {
        updateContributionMode: () => {},
        onInputLanguageChange: () => {},
        openContentSettings: () => {},
        saveContent: (() => {}) as any,
        updateContent: (() => {}) as any,
        publishContent: (() => {}) as any,
    },
    state: initialContentEditorState,
    widgetPaths: {},
    onTitleChange: () => {},
    isCreation: false,
});
